/**
 * Reading order for the fields overlaid on a rendered PDF page.
 *
 * A PDF lists its fields in the order the form was authored, which is not where
 * they sit on the page, so anything that follows that order -- most visibly the
 * Tab key, which follows DOM order -- appears to jump around the page.
 */

/** Tops within this many PDF points belong to the same visual row. */
export const ROW_TOLERANCE_POINTS = 9.5;

export type ReadingOrderPosition = {
  top: number;
  left: number;
};

/**
 * Order items left-to-right, top-to-bottom.
 *
 * Rows are bucketed rather than compared with a tolerance, because a comparator
 * of the form `abs(topA - topB) > tolerance ? topA - topB : leftA - leftB` is not
 * transitive: fields whose tops step across the tolerance in a chain compare
 * inconsistently, and the result then depends on which pivots the sort happens to
 * pick. Bucketing gives one answer regardless.
 *
 * Sorting is stable, so items sharing a position keep their original order.
 */
export function sortIntoReadingOrder<T>(
  items: T[],
  position: (item: T) => ReadingOrderPosition,
  rowTolerance: number = ROW_TOLERANCE_POINTS
): T[] {
  const byTop = [...items].sort((a, b) => position(a).top - position(b).top);

  const rows: T[][] = [];
  let rowTop = 0;
  for (const item of byTop) {
    const { top } = position(item);
    // The anchor is the row's first (highest) item, so a row can never grow
    // taller than the tolerance by drifting one item at a time.
    if (rows.length === 0 || top > rowTop + rowTolerance) {
      rows.push([item]);
      rowTop = top;
    } else {
      rows[rows.length - 1].push(item);
    }
  }

  return rows.flatMap((row) =>
    row.sort((a, b) => position(a).left - position(b).left)
  );
}
